import React, { useState, useEffect } from "react";
import { Check, X, Users } from "lucide-react";
import { OrganizationOverview } from "../../../../services/UserService";
import { formatRole } from "../../../../utils/RoleUtils";

interface OrganizationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  organizationOverview?: OrganizationOverview;
  handleSwitchOrganization: (orgId: string) => void;
}

const OrganizationDropdown: React.FC<OrganizationDropdownProps> = ({
  isOpen,
  onClose,
  organizationOverview,
  handleSwitchOrganization,
}) => {
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowDropdown(true);
    } else {
      setTimeout(() => {
        setShowDropdown(false);
        setSelectedOrgId(null);
      }, 250);
    }
  }, [isOpen]);

  if (!showDropdown) return null;

  const handleSelectOrganization = (orgId: string) => {
    const currentOrgId = organizationOverview?.currentOrganization.id;
    setSelectedOrgId(orgId === currentOrgId ? null : orgId);
  };

  const handleConfirmSwitch = () => {
    if (selectedOrgId) {
      handleSwitchOrganization(selectedOrgId);
      setSelectedOrgId(null);
      onClose();
    }
  };

  return (
    <div
      className={`fixed top-0 right-0 h-screen w-80 bg-background-paper dark:bg-background-panelDark shadow-2xl border-l border-divider dark:border-divider-dark flex flex-col rounded-lg overflow-hidden z-50 
        transition-transform duration-250 ease-in-out ${
          isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        }`}
    >
      <div className="flex items-center justify-between px-5 py-4 bg-background-default dark:bg-background-dark border-b border-divider dark:border-divider-dark">
        <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark">
          Switch Organization
        </h3>
        <button
          className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
          onClick={onClose}
        >
          <X className="w-5 h-5 text-text-primary dark:text-text-primaryDark" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide px-4 py-3 space-y-2">
        {organizationOverview?.associatedOrganizations?.length ? (
          organizationOverview.associatedOrganizations.map((org) => {
            const isCurrent =
              org.id === organizationOverview?.currentOrganization.id;
            const isSelected = org.id === selectedOrgId;

            return (
              <div
                key={org.id}
                onClick={() => handleSelectOrganization(org.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md cursor-pointer transition-all duration-200 shadow-sm
                  ${
                    isCurrent
                      ? "bg-primary/20 dark:bg-primary/30 text-primary font-semibold border-l-4 border-primary"
                      : isSelected
                      ? "bg-secondary/20 dark:bg-secondary/30 text-secondary font-semibold border-l-4 border-secondary"
                      : "hover:bg-background-panel dark:hover:bg-background-panelDark"
                  }
                `}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary/10 dark:bg-secondary/20">
                  <Users className="w-5 h-5 text-secondary dark:text-secondaryDark" />
                </div>

                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary dark:text-text-primaryDark truncate">
                    {org.name}
                  </p>
                  <p className="text-xs text-text-secondary dark:text-text-secondaryDark">
                    {formatRole(org.role) || "Unknown"}
                  </p>
                </div>

                {isCurrent ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : isSelected ? (
                  <Check className="w-4 h-4 text-secondary" />
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="flex items-center justify-center h-24 text-sm text-text-secondary dark:text-text-secondaryDark">
            No organizations found
          </div>
        )}
      </div>

      <div className="p-4 border-t border-divider dark:border-divider-dark">
        <button
          className={`w-full py-2 text-sm font-semibold rounded-md transition-all shadow-md ${
            selectedOrgId
              ? "bg-secondary text-white font-bold border-b-2 border-secondary hover:bg-secondary/90"
              : "bg-secondary/30 text-text-secondary dark:text-text-secondaryDark cursor-not-allowed"
          }`}
          onClick={handleConfirmSwitch}
          disabled={!selectedOrgId}
        >
          Confirm Switch
        </button>
      </div>
    </div>
  );
};

export default OrganizationDropdown;
